import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@mui/material';
import { forwardRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import type { BoxProps } from '@mui/material';
import type { ReactNode } from 'react';
import type { FieldValues, SubmitHandler, UseFormProps, UseFormReturn } from 'react-hook-form';
import type { ParseParams, TypeOf, ZodType } from 'zod';

export declare namespace form {
  interface FormProps<Tzod extends ZodType = ZodType> {
    children: ReactNode;
    schema: Tzod;
    onValid: Parameters<
      UseFormReturn<
        TypeOf<Tzod> & {
          reset: UseFormReturn['reset'];
        }
      >['handleSubmit']
    >[0];
    mode?: UseFormProps['mode'];
    reValidateMode?: UseFormProps['reValidateMode'];
    defaultValues?: UseFormProps['defaultValues'];
    values?: UseFormProps['values'];
    resetOptions?: UseFormProps['resetOptions'];
    shouldFocusError?: UseFormProps['shouldFocusError'];
    shouldUnregister?: UseFormProps['shouldUnregister'];
    shouldUseNativeValidation?: UseFormProps['shouldUseNativeValidation'];
    progressive?: UseFormProps['progressive'];
    criteriaMode?: UseFormProps['criteriaMode'];
    delayError?: UseFormProps['delayError'];
    onInValid?: Parameters<UseFormReturn<TypeOf<Tzod>>['handleSubmit']>[1];
    schemaOptions?: Partial<ParseParams> | undefined;
    factoryOptions?:
      | {
        mode?: 'async' | 'sync' | undefined;
        rawValues?: boolean | undefined;
      }
      | undefined;
  }
}

export const Form = forwardRef<
  HTMLFormElement,
  form.FormProps & BoxProps
>(
  function Form({
    children,
    onValid,
    onInValid,
    schema,
    schemaOptions,
    factoryOptions,
    mode = 'onSubmit',
    reValidateMode = 'onChange',
    defaultValues,
    values,
    resetOptions,
    shouldFocusError,
    shouldUnregister,
    shouldUseNativeValidation,
    progressive,
    criteriaMode,
    delayError,
    ...props
  }, ref) {
    const methods = useForm( {
      mode,
      resolver: zodResolver( schema, schemaOptions, factoryOptions ),
      reValidateMode,
      defaultValues,
      values,
      resetOptions,
      shouldFocusError,
      shouldUnregister,
      shouldUseNativeValidation,
      progressive,
      criteriaMode,
      delayError,
    } );

    const handleValidation: SubmitHandler<FieldValues> = (data: FieldValues, event) =>
      onValid( {
        ...data,
        reset: methods.reset,
      }, event );

    return (
      <FormProvider {...methods}>
        <Box
          ref={ref}
          component="form"
          onSubmit={methods.handleSubmit( handleValidation, onInValid )}
          {...props}
        >
          {children}
        </Box>
      </FormProvider>
    );
  }
);
